import { waveformLayout, waveformAxis, waveformConfig, waveformClusterColor } from '../utils/waveformStyle';
import React, { useMemo, useState } from 'react';
import Plot from './ThemedPlot';
import { WaveformModeSelector, NormalizedWaveformPlot } from './WaveformDisplay';
import { meanWaveformSeries, spikeWaveformSeries } from '../utils/waveformPlots';

const ClusterWaveformPanel = ({ clusterId, waveforms = [], highlightedSpike, initialMode = 'absolute', amplitudeRange, referenceAmplitude = null }) => {
  const [mode, setMode] = useState(initialMode);
  const series = useMemo(() => spikeWaveformSeries(waveforms), [waveforms]);
  const selectedIds = highlightedSpike && String(highlightedSpike.clusterId) === String(clusterId)
    ? [highlightedSpike.spikeIndex] : [];
  const color = waveformClusterColor(clusterId);
  const mean = useMemo(() => meanWaveformSeries(clusterId, waveforms), [clusterId, waveforms]);
  const traces = series.map(row => ({
    type: 'scatter', mode: 'lines', x: row.x, y: row.y, name: row.label,
    customdata: row.x.map(() => row.id), showlegend: false,
    line: { color, width: selectedIds.includes(row.id) ? 3 : 1 },
    opacity: selectedIds.includes(row.id) ? 1 : 0.15,
    hovertemplate: `${row.label}<br>Time: %{x:.3f} ms<br>Amplitude: %{y:.2f}<extra></extra>`,
  }));
  if (mean) traces.push({ type: 'scatter', mode: 'lines', x: mean.x, y: mean.y,
    name: 'Mean', line: { color, width: 4 }, showlegend: false,
    hovertemplate: 'Mean<br>Time: %{x:.3f} ms<br>Amplitude: %{y:.2f}<extra></extra>',
  });

  return <section className="cluster-waveform-panel" aria-label={`Waveforms for cluster ${clusterId}`}>
    <header className="cluster-waveform-panel-header">
      <strong>Cluster {clusterId}</strong>
      <span>{series.length} sampled spike{series.length === 1 ? '' : 's'}</span>
    </header>
    <WaveformModeSelector value={mode} onChange={setMode} populationLabel="Z-scored spikes"
      ariaLabel={`Waveform display for cluster ${clusterId}`} />
    <div className="cluster-waveform-panel-plot">
      {!series.length ? <div className="no-data-message">No waveform samples available for this cluster.</div>
        : mode !== 'absolute' ? <NormalizedWaveformPlot mode={mode} series={series}
          rowLabel="Spike" selectedIds={selectedIds} showLegend={false} />
        : <Plot data={traces}
          layout={{ ...waveformLayout(),
            yaxis: { ...waveformAxis('Waveforms'), range: amplitudeRange },
            shapes: referenceAmplitude === null ? [] : [{ type: 'line', xref: 'paper', x0: 0, x1: 1,
              yref: 'y', y0: referenceAmplitude, y1: referenceAmplitude,
              line: { color: '#e9a23b', width: 2, dash: 'dash' } }],
          }}
          config={waveformConfig}
          style={{ width: '100%', height: '100%' }} useResizeHandler />}
    </div>
  </section>;
};

export default ClusterWaveformPanel;
